import { Banknote, TrendingUp, CreditCard, Rocket } from "lucide-react";
import { passosLabel } from "../../lib/cedenteOperations";
import { cedentePath } from "../../lib/portal";
import { MONEY_CENTS_HINT } from "../../lib/numberFormat";

// R5 — constantes de módulo e configuração por MODO do wizard, extraídas
// MECANICAMENTE de pages/NewOperation.jsx (texto verbatim, mesma ordem).

const ICON_MAP = { "trending-up": TrendingUp, banknote: Banknote, "credit-card": CreditCard, rocket: Rocket };

// Campos MONETÁRIOS do recebível: a edição deles pode ser de FORMA e não de
// VALOR (apagar o zero final de "R$ 9.500,00" muda a string, não o número).
// Invalidar a simulação nesse caso a apagaria SEM trazê-la de volta — o
// `simSignature` continua igual, então não há re-simulação —, e o usuário fica
// olhando para uma tela sem resultado. Ver `updateReceivable`.
const CAMPOS_DE_VALOR = ["net_face_value", "requested_advance_value"];

// FS-162 — campos TRANSITÓRIOS de UI da linha, que não são dado da operação:
// `_pctInput` é o texto do campo "Percentual antecipado" enquanto ele está em
// foco (null = o % volta a ser DERIVADO do valor) e `_advanceTouched` marca que
// alguém já decidiu o valor a antecipar. Nenhum dos dois vai ao payload, e
// escrever neles NUNCA invalida a simulação — não há número novo para simular.
const CAMPOS_UI_ONLY = ["_pctInput", "_advanceTouched"];


// Fluxo de etapas por TIPO de cedente (item 22): PJ ganha a etapa Assinantes
// (o cedente PJ assina o contrato através dos representantes — 1 a 7); PF
// assina ele mesmo (single-signer na V2) e NÃO vê a etapa.
const STEP_LABELS = {
  produto: "Produto",
  cedente: "Cedente & Taxa",
  recebiveis: "Recebiveis",
  assinantes: "Assinantes",
  revisao: "Revisao & Envio",
};
const FLOW_PJ = ["produto", "cedente", "recebiveis", "assinantes", "revisao"];
const FLOW_PF = ["produto", "cedente", "recebiveis", "revisao"];
// Modo cedente: o cedente É o usuário logado (`GET /cedente/me/cedente`) e não
// escolhe taxa — a etapa "Cedente & Taxa" simplesmente não existe.
const FLOW_CEDENTE_PJ = ["produto", "recebiveis", "assinantes", "revisao"];
const FLOW_CEDENTE_PF = ["produto", "recebiveis", "revisao"];

// Códigos de 422 de assinantes da V2 (fail-closed no create): mensagem já vem
// acionável em PT — o wizard exibe INTACTA e volta o usuário à etapa Assinantes.
const SIGNER_ERROR_RE = /signers_required|invalid_signer|too_many_signers|signer_incomplete_for_contract|signers_incomplete/;

// Modo cedente: sem PATCH, não há como completar o cadastro do representante —
// e mandar "cadastre um novo" instruiria o impossível (mesmo CPF ⇒ 409). Copy
// PROVISÓRIA e honesta; a via definitiva é decisão do owner (item 09.1).
const REPRESENTANTE_INCOMPLETO_COPY =
  "Este representante está com o cadastro incompleto. A edição de representantes " +
  "estará disponível em breve.";

/**
 * Configuração por MODO do wizard.
 *
 * - `partner` (default): fluxo histórico do originador — escolhe o cedente,
 *   pode informar taxa, vê faixa/desconto/taxa efetiva.
 * - `cedente`: o próprio cedente sobe a operação. Decisão 01(b) do owner —
 *   MODO REDUZIDO: nenhuma superfície mostra taxa ou percentual. O sacado deixa
 *   de ser digitação livre e passa a ser um cliente do cadastro do cedente.
 *
 * O que muda fica TODO aqui (endpoints, fluxo, copy, o que se exibe); o corpo
 * do componente é o mesmo nos dois modos.
 */
const MODE_CFG = {
  partner: {
    hasCedenteStep: true,
    // Exibe taxa/percentual/faixa/desconto em qualquer superfície.
    showRates: true,
    // Sacado por Select de clientes do cedente (vs. digitação livre).
    payerFromClientes: false,
    // Edição/complemento de representante (PATCH). Não existe no ramo cedente.
    canEditRepresentative: true,
    flowPJ: FLOW_PJ,
    flowPF: FLOW_PF,
    backPath: () => "/operacoes",
    endpoints: {
      products: "/products",
      simulate: "/v2/simulate",
      create: "/operations",
      repBase: (cedenteId) => `/v2/cedentes/${cedenteId}`,
    },
    copy: {
      titulo: "Nova Operacao",
      subtitulo: (totalSteps) =>
        `Crie uma operacao de antecipacao de recebiveis ou liberacao de credito ${passosLabel(totalSteps)}.`,
      voltar: "Voltar para Operacoes",
      produtosHint:
        "Os produtos habilitados para o seu parceiro pelo Backoffice Zemo aparecem coloridos; " +
        "os demais ficam em cinza (indisponiveis).",
      produtosBoUnavailable:
        "Não foi possível carregar seus produtos agora. Tente novamente em alguns minutos.",
      produtosNoneEnabled:
        "Nenhum produto está habilitado para o seu cadastro. Fale com o seu gerente para liberar produtos.",
      assinantesHint: (max) =>
        `O cedente PJ assina através dos representantes. Selecione de 1 a ${max} — ` +
        "a ordem da seleção é a ordem de assinatura. Os assinantes padrão do cadastro vêm pré-selecionados.",
      loadLabels: { me: "os cedentes" },
      // BYTE-IDÊNTICA à versão pré-parametrização (round 4 / B1): o modo
      // parceiro não pode mudar de tela por causa da feature do cedente.
      semRecebiveis:
        "Adicione ao menos um recebivel para criar a operacao. O valor e o prazo sao calculados " +
        "a partir deles, e a simulacao V2 roda automaticamente quando os dados ficam completos.",
      recebiveisHint:
        "Adicione os recebiveis que compoem esta operacao. Campos seguem o schema V2.",
      // Rótulo do campo `net_face_value` — FS-159 (owner 2026-08-21, P1/P2/P3):
      // o parceiro sai de "Valor liquido", que na MESMA tela também nomeava o
      // líquido PAGO (resultado da simulação, `opr_net_present_liquid_value`).
      // Nome canônico do vault (NFV): "Valor de Face Líquido". Ver o comentário
      // do modo cedente para a superseção do item 09.2.
      valorNotaLabel: "Valor de Face Líquido (R$)",
      valorNotaLabelCurto: "Valor de Face Líquido",
      valorNotaHelp: "Já líquido de Impostos e Glosa.",
      // FS-162/Q7 (owner 2026-08-21): o campo existe nos DOIS modos, com o
      // rótulo parametrizado aqui como todo o resto da copy.
      percentualLabel: "Percentual antecipado (%)",
      percentualLabelCurto: "% antecipado",
      // Acentuação: a frase inteira em PT-BR correto. Ela passou a citar o
      // rótulo novo ("Valor de Face Líquido", acentuado por decisão do owner) e
      // meia-frase acentuada dentro de outra sem acento fica pior que qualquer
      // das duas — a nota de "byte-idêntico" do modo parceiro é sobre
      // `semRecebiveis`, não sobre esta.
      recebivelParcial:
        "Há recebível incompleto. Preencha todos os campos obrigatórios (ID, sacado, documento, " +
        "valor de face líquido, valor a antecipar, data) ou remova a linha para continuar — " +
        "apenas recebíveis completos são enviados à V2.",
      sucesso: (numero) => `Operacao ${numero} criada e enviada a V2`,
    },
  },
  cedente: {
    hasCedenteStep: false,
    showRates: false,
    payerFromClientes: true,
    canEditRepresentative: false,
    flowPJ: FLOW_CEDENTE_PJ,
    flowPF: FLOW_CEDENTE_PF,
    backPath: () => cedentePath("/operacoes"),
    endpoints: {
      products: "/cedente/products",
      me: "/cedente/me/cedente",
      simulate: "/cedente/operations/simulate",
      create: "/cedente/operations",
      repBase: () => "/cedente",
    },
    copy: {
      titulo: "Nova Operação",
      subtitulo: (totalSteps) => `Antecipe seus recebíveis ${passosLabel(totalSteps)}.`,
      voltar: "Voltar para Operações",
      produtosHint:
        "Os produtos disponíveis para você aparecem coloridos; os demais ficam em cinza (indisponíveis).",
      produtosBoUnavailable:
        "Produtos indisponíveis no momento — não foi possível confirmar quais estão disponíveis " +
        "para você. Tente novamente em instantes.",
      produtosNoneEnabled:
        "Nenhum produto disponível para você no momento. Assim que houver produto " +
        "liberado para o seu cadastro, ele aparece aqui.",
      assinantesHint: (max) =>
        `A sua empresa assina através dos representantes. Selecione de 1 a ${max} — ` +
        "a ordem da seleção é a ordem de assinatura. Os assinantes padrão do cadastro vêm pré-selecionados.",
      loadLabels: { me: "os dados da sua empresa" },
      semRecebiveis:
        "Adicione ao menos um recebível para criar a operação. O valor e o prazo são calculados " +
        "a partir deles, e o valor líquido é recalculado automaticamente quando os dados ficam completos.",
      recebiveisHint: "Adicione os recebíveis que compõem esta operação.",
      // FS-159 (owner 2026-08-21, P1/P2/P3) — SUPERA o item 09.2 (owner
      // 2026-07-31T19:20:25Z).
      //
      // O 09.2 tinha desfeito, SÓ no modo cedente, a colisão de "Valor líquido"
      // nomeando duas coisas na mesma tela (o valor da nota que se INFORMA × o
      // que se RECEBE), batizando o campo de "Valor a Receber do Cliente". A
      // FS-159 vai além e UNIFICA os dois modos no nome canônico do vault
      // (NFV — net face value): "Valor de Face Líquido", com o mesmo texto
      // auxiliar de Impostos e Glosa nos dois. Motivo da superseção: o rótulo
      // do 09.2 descrevia um FLUXO ("a receber do cliente") e não a GRANDEZA,
      // e divergia do parceiro, que seguia com o nome colidente.
      //
      // Inalterado: o RESULTADO da simulação continua "Valor líquido" nos dois
      // modos (grandeza DIFERENTE — líquido presente pago pós-deságio); e
      // NENHUM rename de campo de API — o payload segue com `net_face_value`.
      valorNotaLabel: "Valor de Face Líquido (R$)",
      valorNotaLabelCurto: "Valor de Face Líquido",
      valorNotaHelp: "Já líquido de Impostos e Glosa.",
      // FS-162/Q7 (owner 2026-08-21): o campo existe nos DOIS modos, com o
      // rótulo parametrizado aqui como todo o resto da copy.
      percentualLabel: "Percentual antecipado (%)",
      percentualLabelCurto: "% antecipado",
      recebivelParcial:
        "Há recebível incompleto. Preencha todos os campos obrigatórios (ID, sacado, " +
        "valor de face líquido, valor a antecipar, data) ou remova a linha para continuar — " +
        "apenas recebíveis completos entram na operação.",
      sucesso: (numero) => `Operação ${numero} criada e enviada para aprovação`,
    },
  },
};

// Formata o `detail` de erro do backend/V2 para exibicao no wizard. Cobre:
// string simples, array de erros de validacao (pydantic 422) e o detail
// ESTRUTURADO de regra de negocio da V2 — ex. CET_OUT_OF_BOUNDS
// ({code, effective_rate_pct, min_pct, max_pct, message}), exibido com a
// faixa de taxa permitida.
// ATENÇÃO: só o modo PARCEIRO usa isto — o modo cedente traduz por CÓDIGO
// (lib/cedenteOperations.js), porque este texto cita taxa/faixa.
export const formatV2Detail = (detail, fallback = "Erro ao comunicar com a V2") => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join("; ") || fallback;
  }
  if (typeof detail === "object") {
    if (detail.code === "CET_OUT_OF_BOUNDS") {
      const rate = detail.effective_rate_pct ? ` (taxa efetiva ${detail.effective_rate_pct}%)` : "";
      return (
        `Taxa fora da faixa permitida para este produto${rate}. ` +
        `Faixa permitida: ${detail.min_pct}% a ${detail.max_pct}% a.m. ` +
        `Ajuste a taxa mensal ou os valores dos recebiveis.`
      );
    }
    return detail.message || JSON.stringify(detail);
  }
  return fallback;
};

// ── Item E: campo faltante de recebível fica VERMELHO ──────────────────────
// O repo não tem variante de erro no <Input>/<SelectTrigger> (são o shadcn
// cru, sem `cva`), então a classe entra no ponto de uso. O `cn()` deles usa
// tailwind-merge, que resolve o conflito com o `border-input` da base — sem
// isso a ordem no CSS gerado decidiria a cor, não a ordem das classes.
// Discreto de propósito: só a cor da borda e do anel de foco muda; nada de
// fundo vermelho, que gritaria numa etapa com até N linhas.
const CAMPO_FALTANDO_CLS = "border-red-400 focus-visible:ring-red-400";
const CAMPO_FALTANDO_CLS_SELECT = "border-red-400 focus:ring-red-400";

// FS-160 (UX-2 do /myuxreview): o modo caixa registradora não é adivinhável —
// o 1º dígito pousa nos centavos e o valor "cresce da direita". Uma linha em
// cada campo de dinheiro, também amarrada por `aria-describedby` (o formato de
// entrada tem de existir para leitor de tela, não só para quem vê o campo).
// A copy vem da lib da máscara — a mesma frase no Stock, um lugar só.
const HINT_CENTAVOS = MONEY_CENTS_HINT;

export {
  ICON_MAP,
  CAMPOS_DE_VALOR,
  CAMPOS_UI_ONLY,
  STEP_LABELS,
  SIGNER_ERROR_RE,
  REPRESENTANTE_INCOMPLETO_COPY,
  MODE_CFG,
  CAMPO_FALTANDO_CLS,
  CAMPO_FALTANDO_CLS_SELECT,
  HINT_CENTAVOS,
};
